import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Badge, Button, Card, Col, Form, Row, Spinner, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  checkAvailabilityAPI,
  createMeetingAPI,
  searchAttendeesAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import { companyUrl } from '../../../utils/mhNav';
import { directoryUsersForMeetings } from '../../../mockData/company/meetings';
import styles from './ManualBookingDesk.module.scss';

const ORG_OPTIONS = directoryUsersForMeetings.map((u) => ({
  value: u.id,
  label: `${u.name} (${u.email})`,
  name: u.name,
  email: u.email,
}));

const DURATIONS = [15, 30, 45, 60, 90, 120];

export default function ManualBookingDesk() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [manualSlot, setManualSlot] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [attSearch, setAttSearch] = useState('');
  const [attResults, setAttResults] = useState([]);
  const [linkMode, setLinkMode] = useState('teams');

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subject: '',
      organizerId: '',
      provider: 'Microsoft',
      channel: 'Bot',
      notes: '',
      durationMin: 60,
      datePreference: new Date().toISOString().slice(0, 10),
      manualStart: '',
    },
  });

  const organizerId = watch('organizerId');

  const next = async () => {
    if (step === 0) {
      const ok = await trigger(['subject', 'organizerId']);
      if (!ok) return;
    }
    if (step === 1) {
      const ok = await trigger(['durationMin', 'datePreference']);
      if (!ok) return;
    }
    if (step === 2) {
      if (!selectedSlot && !watch('manualStart')) {
        toast.error('Pick a slot or enter manual time');
        return;
      }
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const runAvailability = async () => {
    setSlotsLoading(true);
    try {
      const res = await checkAvailabilityAPI({
        attendees: attendees.map((a) => a.id),
        durationMin: watch('durationMin'),
        datePreference: watch('datePreference'),
      });
      setSlots(res.data);
    } finally {
      setSlotsLoading(false);
    }
  };

  const searchAtt = async (q) => {
    setAttSearch(q);
    const res = await searchAttendeesAPI(q);
    setAttResults(res.data || []);
  };

  const onCreate = async (data) => {
    const org = ORG_OPTIONS.find((o) => o.value === data.organizerId);
    const startIso = selectedSlot
      ? new Date(`${watch('datePreference')}T${selectedSlot.time}:00`).toISOString()
      : new Date(data.manualStart).toISOString();
    const dur = Number(data.durationMin);
    const endIso = new Date(new Date(startIso).getTime() + dur * 60000).toISOString();
    const res = await createMeetingAPI({
      subject: data.subject,
      organizerId: data.organizerId,
      organizerName: org?.name,
      organizerEmail: org?.email,
      provider: data.provider === 'Microsoft' ? 'Microsoft' : 'Google',
      channel: data.channel === 'Bot' ? 'Teams' : data.channel === 'Web Portal' ? 'Web' : 'Teams',
      notes: data.notes,
      startAt: startIso,
      endAt: endIso,
      durationMin: dur,
      attendeeIds: attendees.map((a) => a.id),
    });
    toast.success('Meeting created');
    navigate(companyUrl(`meetings/${res.data.id}`));
  };

  return (
    <div>
      <PageHeader
        title="Manual booking desk"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Meetings', path: companyUrl('meetings') },
          { label: 'New' },
        ]}
        secondaryActions={[{ label: 'Cancel', onClick: () => navigate(companyUrl('meetings')) }]}
      />

      <div className="mb-3 small text-muted">Step {step + 1} of 4</div>

      {step === 0 && (
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Basics</h5>
            <Form.Group className="mb-2">
              <Form.Label>Subject *</Form.Label>
              <Form.Control {...register('subject', { required: true })} />
              {errors.subject && <div className="text-danger small">Required</div>}
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Organizer *</Form.Label>
              <Select
                options={ORG_OPTIONS}
                value={ORG_OPTIONS.find((o) => o.value === organizerId) || null}
                onChange={(opt) => setValue('organizerId', opt?.value || '', { shouldValidate: true })}
                placeholder="Select user…"
              />
              <input type="hidden" {...register('organizerId', { required: true })} />
              {errors.organizerId && <div className="text-danger small">Required</div>}
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Provider</Form.Label>
              <div>
                <Form.Check inline type="radio" label="Teams" value="Microsoft" {...register('provider')} />
                <Form.Check inline type="radio" label="Google Meet" value="Google" {...register('provider')} />
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Channel</Form.Label>
              <div>
                <Form.Check inline type="radio" label="Bot" value="Bot" {...register('channel')} />
                <Form.Check inline type="radio" label="Web Portal" value="Web Portal" {...register('channel')} />
                <Form.Check inline type="radio" label="Calendar" value="Calendar" {...register('channel')} />
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Notes / agenda</Form.Label>
              <Form.Control as="textarea" rows={3} {...register('notes')} />
            </Form.Group>
          </Card.Body>
        </Card>
      )}

      {step === 1 && (
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Attendees</h5>
            <Form.Group className="mb-2">
              <Form.Label>Search directory</Form.Label>
              <Form.Control value={attSearch} onChange={(e) => searchAtt(e.target.value)} placeholder="Type to search…" />
              <div className={`mt-2 d-flex flex-wrap gap-1 ${styles.chips}`}>
                {attResults.map((a) => (
                  <Button
                    key={a.id}
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      if (!attendees.find((x) => x.id === a.id)) setAttendees((p) => [...p, a]);
                    }}
                  >
                    + {a.name}
                    {a.conflict && <Badge bg="warning" className="ms-1">conflict</Badge>}
                  </Button>
                ))}
              </div>
            </Form.Group>
            <div className="mb-3 d-flex flex-wrap gap-2">
              {attendees.map((a) => (
                <Badge key={a.id} bg="primary" className="d-flex align-items-center gap-1 py-2 px-2">
                  {a.name}
                  {a.conflict && <span className="text-warning">⚠</span>}
                  <button type="button" className="btn-close btn-close-white btn-sm ms-1" aria-label="Remove" onClick={() => setAttendees((p) => p.filter((x) => x.id !== a.id))} />
                </Badge>
              ))}
            </div>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Duration</Form.Label>
                  <Form.Select {...register('durationMin', { required: true })}>
                    {DURATIONS.map((d) => (
                      <option key={d} value={d}>
                        {d} min
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Date preference</Form.Label>
                  <Form.Control type="date" {...register('datePreference', { required: true })} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {step === 2 && (
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Slot selection</h5>
            <Button className="mb-3" variant="outline-primary" onClick={runAvailability} disabled={slotsLoading}>
              {slotsLoading ? <Spinner size="sm" animation="border" /> : null} Check availability
            </Button>
            <Form.Check
              type="switch"
              label="Manual slot override"
              checked={manualSlot}
              onChange={(e) => setManualSlot(e.target.checked)}
              className="mb-2"
            />
            {manualSlot && (
              <Form.Group className="mb-3">
                <Form.Label>Start (local)</Form.Label>
                <Form.Control type="datetime-local" {...register('manualStart')} />
              </Form.Group>
            )}
            {slots && (
              <>
                <div className="fw-semibold mb-2">Suggested slots</div>
                <ul className="list-unstyled">
                  {slots.suggested.map((s) => (
                    <li key={s.id} className="mb-2">
                      <Button
                        variant={selectedSlot?.id === s.id ? 'primary' : 'light'}
                        className="text-start w-100 border"
                        onClick={() => setSelectedSlot(s)}
                      >
                        {s.day} {s.time} · {s.score}
                        {s.conflicts?.length > 0 && (
                          <div className="small text-warning">{s.conflicts.join('; ')}</div>
                        )}
                      </Button>
                    </li>
                  ))}
                </ul>
                <div className="fw-semibold mb-2">Alternative slots</div>
                <ul className="list-unstyled">
                  {slots.alternatives.map((s) => (
                    <li key={s.id} className="mb-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => setSelectedSlot(s)}>
                        {s.day} {s.time}
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {step === 3 && (
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Confirm</h5>
            <div className="border rounded p-3 mb-3">
              <div><strong>{watch('subject')}</strong></div>
              <div className="small text-muted">Organizer: {ORG_OPTIONS.find((o) => o.value === watch('organizerId'))?.label}</div>
              <div>Attendees: {attendees.map((a) => a.name).join(', ') || '—'}</div>
              <div>
                {selectedSlot
                  ? `${selectedSlot.day} ${selectedSlot.time}`
                  : watch('manualStart')}
                {' · '}
                {watch('durationMin')} min
              </div>
              <div>Provider: {watch('provider')}</div>
            </div>
            <div className="mb-2">Preview join link</div>
            <ToggleButtonGroup type="radio" name="link" value={linkMode} onChange={setLinkMode}>
              <ToggleButton id="t1" value="teams" variant="outline-primary" size="sm">
                Teams
              </ToggleButton>
              <ToggleButton id="t2" value="meet" variant="outline-primary" size="sm">
                Meet
              </ToggleButton>
            </ToggleButtonGroup>
            <div className="small text-muted mt-2">
              Mock link: https://{linkMode}.example/booking
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="d-flex gap-2">
        {step > 0 && <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>Back</Button>}
        {step < 3 && <Button onClick={next}>Next</Button>}
        {step === 3 && (
          <Button onClick={handleSubmit(onCreate)}>Create Meeting</Button>
        )}
      </div>
    </div>
  );
}
